// ─────────────────────────────────────────────────────────────────
// Glass-i — Production Department Master helpers
// (Stage 6B.8A-3K.3 + 3K.6 execution-order extension).
//
// Pure JS — tested by the frontend node:test runner.
// ─────────────────────────────────────────────────────────────────

export const DEPARTMENT_TYPES = Object.freeze([
  { value: 'main',    label: 'Main' },
  { value: 'sub',     label: 'Sub' },
  { value: 'qc',      label: 'QC' },
  { value: 'support', label: 'Support' }
]);

export const WORK_MODES = Object.freeze([
  { value: 'manual',         label: 'Manual' },
  { value: 'machine',        label: 'Machine' },
  { value: 'mixed',          label: 'Mixed' },
  { value: 'outsourced',     label: 'Outsourced' },
  { value: 'not_applicable', label: 'Not Applicable' }
]);

// ── Stage 6B.8A-3K.6 — Execution flow vocabulary ────────────────
// Mirrors backend EXECUTION_GROUPS + DEFAULT_EXECUTION_FLOW so the
// page renders the canonical order even if the API is slow.
export const EXECUTION_GROUPS = Object.freeze([
  { value: 'cutting',            label: 'Cutting' },
  { value: 'polish',             label: 'Polish' },
  { value: 'fabrication',        label: 'Fabrication' },
  { value: 'washing',            label: 'Washing' },
  { value: 'tempering',          label: 'Tempering' },
  { value: 'lami',               label: 'Lami / Lamination' },
  { value: 'dgu',                label: 'DGU' },
  { value: 'design',             label: 'Design' },
  { value: 'ready_for_dispatch', label: 'Ready for Dispatch' },
  { value: 'qc',                 label: 'QC' },
  { value: 'dispatch',           label: 'Dispatch' },
  { value: 'support',            label: 'Support' },
  { value: 'other',              label: 'Other' }
]);

// Canonical preview flow used by ExecutionFlowView's fallback when
// the API returns no rows (fresh install, ACL denial, network blip).
export const DEFAULT_EXECUTION_FLOW = Object.freeze([
  { group: 'cutting',            order: 10,  label: 'Cutting' },
  { group: 'polish',             order: 20,  label: 'Polish' },
  { group: 'fabrication',        order: 30,  label: 'Fabrication' },
  { group: 'washing',            order: 40,  label: 'Washing' },
  { group: 'tempering',          order: 50,  label: 'Tempering' },
  { group: 'lami',               order: 60,  label: 'Lami / Lamination' },
  { group: 'dgu',                order: 70,  label: 'DGU' },
  { group: 'design',             order: 80,  label: 'Design' },
  { group: 'ready_for_dispatch', order: 90,  label: 'Ready for Dispatch' },
  { group: 'qc',                 order: 100, label: 'QC' },
  { group: 'dispatch',           order: 110, label: 'Dispatch' }
]);

export function executionGroupLabel(value) {
  const opt = EXECUTION_GROUPS.find((o) => o.value === value);
  return opt ? opt.label : '';
}

// ── Build a tree from a flat list of department rows ────────────
// Returns an array of root nodes; each node carries a `children: []`
// array. Subs whose parent is missing from the input are surfaced as
// orphans at the top level so nothing silently disappears.
export function buildDepartmentTree(rows) {
  if (!Array.isArray(rows)) return [];
  const byId    = new Map();
  const cloned  = rows.map((r) => ({ ...r, children: [] }));
  for (const r of cloned) byId.set(r.id, r);
  const roots = [];
  for (const r of cloned) {
    if (r.parentDepartmentId && byId.has(r.parentDepartmentId)) {
      byId.get(r.parentDepartmentId).children.push(r);
    } else {
      roots.push(r);
    }
  }
  // Sort children + roots by execution_order (nulls last), then
  // sequenceNo, then code, so a fresh tenant still renders in the
  // preferred production flow even before any reorder.
  const sortFn = (a, b) => {
    const ao = a.executionOrder ?? Number.POSITIVE_INFINITY;
    const bo = b.executionOrder ?? Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    const as = Number(a.sequenceNo || 0);
    const bs = Number(b.sequenceNo || 0);
    if (as !== bs) return as - bs;
    return String(a.departmentCode || '')
      .localeCompare(String(b.departmentCode || ''));
  };
  function sortRecursive(nodes) {
    nodes.sort(sortFn);
    for (const n of nodes) sortRecursive(n.children);
  }
  sortRecursive(roots);
  return roots;
}

// Flatten a tree back to a list with `depth` added on each node.
// Used by the table renderer to indent sub rows visually.
export function flattenDepartmentTree(tree, depth = 0) {
  const out = [];
  if (!Array.isArray(tree)) return out;
  for (const n of tree) {
    const { children = [], ...row } = n;
    out.push({ ...row, depth });
    if (children.length) out.push(...flattenDepartmentTree(children, depth + 1));
  }
  return out;
}

// ── Friendly label helpers ──────────────────────────────────────
export function departmentLabel(row) {
  if (!row) return '';
  if (row.abbreviation && String(row.abbreviation).trim()) {
    return `${row.departmentName} (${row.abbreviation})`;
  }
  return row.departmentName || row.departmentCode || '';
}

export function workModeLabel(value) {
  const opt = WORK_MODES.find((o) => o.value === value);
  return opt ? opt.label : '';
}

export function departmentTypeLabel(value) {
  const opt = DEPARTMENT_TYPES.find((o) => o.value === value);
  return opt ? opt.label : '';
}

// ── Payload builder for create / update ─────────────────────────
// Strips empty strings; coerces booleans + numbers.
export function buildDepartmentPayload(form) {
  if (!form || typeof form !== 'object') return {};
  const out = {
    departmentName:     trimOrNull(form.departmentName),
    departmentCode:     trimOrNull(form.departmentCode),
    abbreviation:       trimOrNull(form.abbreviation),
    departmentType:     trimOrNull(form.departmentType) || 'main',
    workMode:           trimOrNull(form.workMode),
    parentDepartmentId: trimOrNull(form.parentDepartmentId),
    sequenceNo:         toIntOrDefault(form.sequenceNo, 1),
    isProductionStage:  form.isProductionStage !== false,
    isQcStage:          form.isQcStage === true,
    isActive:           form.isActive !== false,
    remarks:            trimOrNull(form.remarks),
    // ── Stage 6B.8A-3K.6 — execution-order fields ─────────────
    executionOrder:     toIntOrNull(form.executionOrder),
    executionGroup:     trimOrNull(form.executionGroup),
    isRouteStage:       form.isRouteStage !== false,
    allowParallelExecution: form.allowParallelExecution === true
  };
  // Main departments never carry a parent — null it out defensively.
  if (out.departmentType === 'main') out.parentDepartmentId = null;
  // Main departments default work mode to 'not_applicable' when blank.
  if (out.departmentType === 'main' && !out.workMode) {
    out.workMode = 'not_applicable';
  }
  return out;
}

// WO-2C — turn a /removal-impact payload into a blocked flag + human
// reasons, for the disable/archive hard-block dialog. Pure so it can be
// unit-tested without mounting the page.
export function summariseRemovalImpact(impact) {
  const reasons = [];
  const inFlight = impact && impact.inFlight;
  if (inFlight && inFlight.count > 0) {
    const sample = Array.isArray(inFlight.sample) ? inFlight.sample : [];
    reasons.push(
      `${inFlight.count} active work order(s) still route through this stage`
      + (sample.length ? ` (e.g. ${sample.join(', ')})` : '')
      + '. Complete or cancel them first.');
  }
  const orphaned = impact && impact.orphanedTypes;
  if (Array.isArray(orphaned) && orphaned.length) {
    reasons.push(
      `It is the only production stage for glass type(s): ${orphaned.join(', ')}. `
      + 'Add another stage to them first.');
  }
  return { blocked: !!(impact && impact.blocked), reasons };
}

// "Manual Cutting" → "MANUAL_CUTTING"
export function suggestDepartmentCode(name) {
  if (!name) return '';
  const cleaned = String(name).trim().toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return cleaned.slice(0, 30);
}

// "Manual Cutting" → "MC"  /  "Machine Tempering" → "MT"
// Falls back to the first 3 letters of a single-word name.
export function suggestAbbreviation(name) {
  if (!name) return '';
  const cleaned = String(name).trim();
  if (!cleaned) return '';
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return words.slice(0, 4)
      .map((w) => w.charAt(0).toUpperCase())
      .join('');
  }
  return cleaned.slice(0, 3).toUpperCase();
}

// Client-side last-mile check before opening the network call.
// Returns { ok, errors: [{field, message}] }.
export function validateDepartmentForm(form) {
  const errors = [];
  if (!form || typeof form !== 'object') {
    return { ok: false, errors: [{ field: 'form',
      message: 'Department form is required.' }] };
  }
  if (!form.departmentName || !String(form.departmentName).trim()) {
    errors.push({ field: 'departmentName',
      message: 'Department name is required.' });
  }
  const type = form.departmentType || 'main';
  if (!DEPARTMENT_TYPES.find((o) => o.value === type)) {
    errors.push({ field: 'departmentType',
      message: 'Select a valid department type.' });
  }
  if (type === 'sub' && (!form.parentDepartmentId
                       || !String(form.parentDepartmentId).trim())) {
    errors.push({ field: 'parentDepartmentId',
      message: 'Sub department must have a parent department.' });
  }
  if (type === 'main' && form.parentDepartmentId
                      && String(form.parentDepartmentId).trim()) {
    errors.push({ field: 'parentDepartmentId',
      message: 'Main department cannot have a parent department.' });
  }
  if (form.workMode && !WORK_MODES.find((o) => o.value === form.workMode)) {
    errors.push({ field: 'workMode', message: 'Select a valid work mode.' });
  }
  if (form.sequenceNo != null && form.sequenceNo !== ''
   && Number(form.sequenceNo) < 0) {
    errors.push({ field: 'sequenceNo',
      message: 'Sequence number cannot be negative.' });
  }
  if (form.executionOrder != null && form.executionOrder !== ''
   && Number(form.executionOrder) < 0) {
    errors.push({ field: 'executionOrder',
      message: 'Execution order must be zero or greater.' });
  }
  if (form.executionGroup
   && !EXECUTION_GROUPS.find((o) => o.value === form.executionGroup)) {
    errors.push({ field: 'executionGroup',
      message: 'Execution group is invalid.' });
  }
  return { ok: errors.length === 0, errors };
}

// Stable sort for the flat-list view (execution order first).
export function sortDepartments(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => {
    const ao = a.executionOrder ?? Number.POSITIVE_INFINITY;
    const bo = b.executionOrder ?? Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    const as = Number(a.sequenceNo || 0);
    const bs = Number(b.sequenceNo || 0);
    if (as !== bs) return as - bs;
    return String(a.departmentCode || '')
      .localeCompare(String(b.departmentCode || ''));
  });
}

// ── Execution-flow grouping (Stage 6B.8A-3K.6) ──────────────────
// Takes the flat result of /execution-flow and groups it by
// executionGroup + executionOrder so the Execution Flow View can
// render parent + children under one band:
//
//   [{ order: 20, group: 'polish', label: 'Polish',
//      parent: <main row>, alternatives: [<manual>, <machine>] }, …]
//
// Rows without a recognised group fall under 'other'. The output is
// sorted by order.
export function buildExecutionFlowGroups(rows) {
  if (!Array.isArray(rows)) return [];
  const byOrder = new Map(); // key = `${order}::${group}`
  for (const r of rows) {
    if (!r) continue;
    // `Number(null)` is 0 (finite!) — guard explicitly so rows with
    // executionOrder == null/'' don't accidentally land at order 0.
    if (r.executionOrder == null || r.executionOrder === '') continue;
    const order = Number(r.executionOrder);
    if (!Number.isFinite(order)) continue;
    const group = r.executionGroup || 'other';
    const key = `${order}::${group}`;
    if (!byOrder.has(key)) {
      byOrder.set(key, {
        order, group, label: executionGroupLabel(group) || group,
        parent: null, alternatives: []
      });
    }
    const bucket = byOrder.get(key);
    if (!r.parentDepartmentId) {
      // First parent wins; subsequent main-level rows in the same
      // bucket (e.g. Packing vs Ready for Dispatch at order=90) fall
      // into alternatives so nothing disappears.
      if (!bucket.parent) bucket.parent = r;
      else bucket.alternatives.push(r);
    } else {
      bucket.alternatives.push(r);
    }
  }
  const out = Array.from(byOrder.values());
  out.sort((a, b) => a.order - b.order);
  return out;
}

// ── Private helpers ─────────────────────────────────────────────
function trimOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}
function toIntOrDefault(v, fb) {
  if (v === null || v === undefined || v === '') return fb;
  const n = Number(v);
  if (!Number.isFinite(n)) return fb;
  return Math.trunc(n);
}
function toIntOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}
